import { useEffect, useState } from 'react';
import { useTranslation } from '../../i18n';
import { api } from '../../lib/api';
import { CreatePersonModal } from './CreatePersonModal';
import { OrgPicker } from './OrgPicker';
import { PhotoUpload } from '../../components/PhotoUpload';
import { OrganisationsList } from '../organisations/OrganisationsList';
import {
  PERSON_TEAM,
  PERSON_PARTNER,
  PERSON_EXTERNAL,
} from '../../lib/constants';

const TAB_KEYS = ['people', 'organisations'];

const FILTERS = [
  { key: 'all',      labelKey: 'people.filters.all' },
  { key: 'team',     labelKey: 'people.filters.team',     type: PERSON_TEAM },
  { key: 'partners', labelKey: 'people.filters.partners', type: PERSON_PARTNER },
  { key: 'external', labelKey: 'people.filters.external', type: PERSON_EXTERNAL },
];

const TYPES = [PERSON_EXTERNAL, PERSON_TEAM, PERSON_PARTNER];

const SOCIAL_ICONS = {
  linkedin:  '💼',
  instagram: '📷',
  facebook:  '👍',
  twitter:   '🐦',
};

// Fallback used when the Worker is unreachable. Shape matches API response.
const MOCK_PEOPLE = [
  { id: 'mock-1', name: 'Anna Nowak',         role: 'Director',             organisationName: 'F8M Foundation',              type: PERSON_TEAM,     socialHandles: { linkedin: '@anna-nowak',     instagram: '@annanowak' } },
  { id: 'mock-2', name: 'Rushil Kotecha',     role: 'Founder',              organisationName: 'F8M Foundation',              type: PERSON_TEAM,     socialHandles: { linkedin: '@rushil-kotecha' } },
  { id: 'mock-3', name: 'Tomek Zieliński',    role: 'Content Lead',         organisationName: 'F8M Foundation',              type: PERSON_TEAM,     socialHandles: { linkedin: '@tomek-z',        instagram: '@tomekz' } },
  { id: 'mock-4', name: 'Maria Wiśniewska',   role: 'Heritage Policy Lead', organisationName: 'European Commission',         type: PERSON_PARTNER,  socialHandles: { linkedin: '@m-wisniewska' } },
  { id: 'mock-5', name: 'Janusz Zieliński',   role: 'Founder',              organisationName: 'Digital Heritage Foundation', type: PERSON_PARTNER,  socialHandles: { linkedin: '@jzielinski',     facebook: '@jzielinski' } },
  { id: 'mock-6', name: 'Dr. Piotr Kowalski', role: 'Chief Curator',        organisationName: 'National Museum of Kraków',   type: PERSON_EXTERNAL, socialHandles: { linkedin: '@pkowalski' } },
];

function initials(name) {
  return name.split(' ').map((p) => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
}

// ─────────────────────────────────────────────────────────────────────────────
// Inline edit form rendered when a person row is expanded.
// ─────────────────────────────────────────────────────────────────────────────
function PersonEditForm({ person, organisations, onSave, onCancel, onDelete, onOrgsRefresh, saving, error }) {
  const { t } = useTranslation();
  const [photoB64, setPhotoB64] = useState(person.photoB64 || null);
  const [name, setName] = useState(person.name || '');
  const [role, setRole] = useState(person.role || '');
  const [organisationId, setOrganisationId] = useState(person.organisationId || null);
  const [type, setType] = useState(person.type || PERSON_EXTERNAL);
  const [email, setEmail] = useState(person.email || '');
  const [phone, setPhone] = useState(person.phone || '');
  const [linkedin, setLinkedin] = useState(person.socialHandles?.linkedin || '');
  const [instagram, setInstagram] = useState(person.socialHandles?.instagram || '');
  const [facebook, setFacebook] = useState(person.socialHandles?.facebook || '');
  const [notes, setNotes] = useState(person.notes || '');

  const trimmedName = name.trim();
  const canSubmit = trimmedName.length > 0 && !saving;

  function handleSubmit(event) {
    event.preventDefault();
    if (!canSubmit) return;
    const handles = {};
    if (linkedin.trim())  handles.linkedin  = linkedin.trim();
    if (instagram.trim()) handles.instagram = instagram.trim();
    if (facebook.trim())  handles.facebook  = facebook.trim();
    onSave({
      name: trimmedName,
      role: role.trim() || null,
      organisationId: organisationId || null,
      type,
      photoB64: photoB64 || null,
      email: email.trim() || null,
      phone: phone.trim() || null,
      socialHandles: handles,
      notes: notes.trim() || null,
    });
  }

  return (
    <form className="person-edit" onSubmit={handleSubmit} noValidate>
      {/* ── Section 1: Identity ─────────────────────────────────────── */}
      <div className="form-section">
        <h3 className="form-section__title">{t('people.form.sections.identity')}</h3>

        <div className="person-photo-row">
          <PhotoUpload value={photoB64} onChange={setPhotoB64} shape="circle" size={80} />
          <label className="form-field person-photo-row__field">
            <span className="form-field__label">{t('people.form.name')} *</span>
            <input
              className="form-field__input"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </label>
        </div>

        <div className="person-edit__row">
          <label className="form-field">
            <span className="form-field__label">{t('people.form.role')}</span>
            <input
              className="form-field__input"
              type="text"
              value={role}
              placeholder={t('people.form.rolePlaceholder')}
              onChange={(e) => setRole(e.target.value)}
            />
          </label>
          <label className="form-field">
            <span className="form-field__label">{t('people.form.type')}</span>
            <select
              className="form-field__select"
              value={type}
              onChange={(e) => setType(e.target.value)}
            >
              {TYPES.map((typeKey) => (
                <option key={typeKey} value={typeKey}>{t(`people.types.${typeKey}`)}</option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {/* ── Section 2: Organisation ───────────────────────────────────── */}
      <div className="form-section">
        <h3 className="form-section__title">{t('people.form.sections.organisation')}</h3>
        <div className="form-field">
          <span className="form-field__label">{t('people.form.organisation')}</span>
          <OrgPicker
            value={organisationId}
            organisations={organisations}
            onChange={setOrganisationId}
            onCreated={onOrgsRefresh}
          />
        </div>
      </div>

      {/* ── Section 3: Contact ────────────────────────────────────────── */}
      <div className="form-section">
        <h3 className="form-section__title">{t('people.form.sections.contact')}</h3>
        <div className="person-edit__row">
          <label className="form-field">
            <span className="form-field__label">{t('people.form.email')}</span>
            <input
              className="form-field__input"
              type="email"
              value={email}
              placeholder={t('people.form.emailPlaceholder')}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <label className="form-field">
            <span className="form-field__label">{t('people.form.phone')}</span>
            <input
              className="form-field__input"
              type="tel"
              value={phone}
              placeholder={t('people.form.phonePlaceholder')}
              onChange={(e) => setPhone(e.target.value)}
            />
          </label>
        </div>
      </div>

      {/* ── Section 4: Social ─────────────────────────────────────────── */}
      <div className="form-section">
        <h3 className="form-section__title">{t('people.form.sections.social')}</h3>
        <div className="form-field__stack">
          <div className="social-input">
            <span className="social-input__icon" aria-hidden="true">💼</span>
            <input
              className="form-field__input"
              type="text"
              value={linkedin}
              placeholder={t('people.form.linkedinPlaceholder')}
              onChange={(e) => setLinkedin(e.target.value)}
            />
          </div>
          <div className="social-input">
            <span className="social-input__icon" aria-hidden="true">📷</span>
            <input
              className="form-field__input"
              type="text"
              value={instagram}
              placeholder={t('people.form.instagramPlaceholder')}
              onChange={(e) => setInstagram(e.target.value)}
            />
          </div>
          <div className="social-input">
            <span className="social-input__icon" aria-hidden="true">👍</span>
            <input
              className="form-field__input"
              type="text"
              value={facebook}
              placeholder={t('people.form.facebookPlaceholder')}
              onChange={(e) => setFacebook(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* ── Section 5: Context ────────────────────────────────────────── */}
      <div className="form-section">
        <h3 className="form-section__title">{t('people.form.sections.context')}</h3>
        <label className="form-field">
          <span className="form-field__label">{t('people.form.notes')}</span>
          <textarea
            className="form-field__textarea"
            value={notes}
            placeholder={t('people.form.notesPlaceholder')}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
          />
        </label>
      </div>

      <div className="gdpr-note">{t('people.gdprNote')}</div>

      {error && <div className="modal__error" role="alert">{error}</div>}

      <div className="person-edit__actions">
        <button type="submit" className="button button--primary" disabled={!canSubmit}>
          {saving ? t('people.form.submitting') : t('people.form.submitEdit')}
        </button>
        <button type="button" className="button button--ghost" onClick={onCancel} disabled={saving}>
          {t('common.cancel')}
        </button>
        <button
          type="button"
          className="button button--ghost button--ghost--danger"
          onClick={onDelete}
          disabled={saving}
        >
          {t('people.details.remove')}
        </button>
      </div>
    </form>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Confirm-delete dialog (simpler than the event one — no name-typing required)
// ─────────────────────────────────────────────────────────────────────────────
function ConfirmDeleteDialog({ name, onCancel, onConfirm, deleting }) {
  const { t } = useTranslation();
  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={() => !deleting && onCancel()}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2 className="modal__title">{t('people.delete.title')}</h2>
        <p className="modal__body">{t('people.delete.body', { name })}</p>
        <div className="modal__actions">
          <button type="button" className="button button--ghost" onClick={onCancel} disabled={deleting}>
            {t('common.cancel')}
          </button>
          <button type="button" className="button button--danger" onClick={onConfirm} disabled={deleting}>
            {t('people.delete.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// People sub-section (the existing list view, factored out of the page so the
// tab bar can swap it for OrganisationsList)
// ─────────────────────────────────────────────────────────────────────────────
function PeopleSection() {
  const { t } = useTranslation();
  const [filter, setFilter] = useState('all');
  const [people, setPeople] = useState([]);
  const [organisations, setOrganisations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [usingMockData, setUsingMockData] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [orgsRefreshKey, setOrgsRefreshKey] = useState(0);
  const [expandedId, setExpandedId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editError, setEditError] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // People fetch.
  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    const filterDef = FILTERS.find((f) => f.key === filter);
    const filters = filterDef?.type ? { type: filterDef.type } : {};
    api.people
      .list(filters)
      .then((data) => {
        if (cancelled) return;
        setPeople(data?.people ?? []);
        setUsingMockData(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('[people] API unreachable, using mock data:', err.message);
        setPeople(
          filter === 'all'
            ? MOCK_PEOPLE
            : MOCK_PEOPLE.filter((p) => p.type === filterDef?.type),
        );
        setUsingMockData(true);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => { cancelled = true; };
  }, [filter, refreshKey]);

  // Organisations fetch (cached for OrgPicker — refreshes when an org is added).
  useEffect(() => {
    let cancelled = false;
    api.organisations
      .list()
      .then((data) => {
        if (!cancelled) setOrganisations(data?.organisations ?? []);
      })
      .catch(() => {
        if (!cancelled) setOrganisations([]);
      });
    return () => { cancelled = true; };
  }, [orgsRefreshKey]);

  function handleCreated() {
    setShowCreate(false);
    setRefreshKey((k) => k + 1);
  }

  function refreshOrgs() {
    setOrgsRefreshKey((k) => k + 1);
  }

  async function handleSave(id, updates) {
    setEditError(null);
    setSavingId(id);
    try {
      const updated = await api.people.update(id, updates);
      setPeople((prev) => prev.map((p) => (p.id === id ? updated : p)));
      setExpandedId(null);
    } catch (err) {
      setEditError(err?.message || t('people.form.error'));
    } finally {
      setSavingId(null);
    }
  }

  async function handleDelete(id) {
    setDeleting(true);
    try {
      await api.people.delete(id);
      setPeople((prev) => prev.filter((p) => p.id !== id));
      setExpandedId((prev) => (prev === id ? null : prev));
      setDeleteTarget(null);
    } catch (err) {
      console.error('[people] delete failed:', err);
      setEditError(err?.message || t('people.form.error'));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <>
      <div
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 'var(--space-3)', marginBottom: 'var(--space-4)' }}
      >
        <div className="page-header__subtitle">{t('people.subtitle')}</div>
        <button type="button" className="button button--primary" onClick={() => setShowCreate(true)}>
          + {t('people.addPerson')}
        </button>
      </div>

      <div className="filter-pills" role="tablist" style={{ marginBottom: 'var(--space-4)' }}>
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            role="tab"
            aria-selected={filter === f.key}
            className={'filter-pill' + (filter === f.key ? ' filter-pill--active' : '')}
            onClick={() => { setFilter(f.key); setExpandedId(null); }}
          >
            {t(f.labelKey)}
          </button>
        ))}
      </div>

      {usingMockData && <div className="dev-notice">{t('people.loadError')}</div>}

      {isLoading ? (
        <div className="empty-state">
          <div className="empty-state__icon" aria-hidden="true">⏳</div>
          <p>{t('people.loading')}</p>
        </div>
      ) : people.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state__icon" aria-hidden="true">👥</div>
          <p>{t('people.noResults')}</p>
        </div>
      ) : (
        <div className="people-list">
          {people.map((person) => {
            const isOpen = expandedId === person.id;
            const handles = Object.entries(person.socialHandles || {});
            const orgLabel = person.organisationName || person.organisation || null;
            return (
              <div key={person.id}>
                <button
                  type="button"
                  className="person-row"
                  onClick={() => {
                    setExpandedId(isOpen ? null : person.id);
                    setEditError(null);
                  }}
                  aria-expanded={isOpen}
                >
                  <div className="person-row__avatar">
                    {person.photoB64 ? (
                      <img src={person.photoB64} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                    ) : (
                      initials(person.name)
                    )}
                  </div>
                  <div className="person-row__body">
                    <div className="person-row__name">{person.name}</div>
                    <div className="person-row__meta">
                      {person.role || '—'}
                      {orgLabel ? ` · ${orgLabel}` : ''}
                    </div>
                  </div>
                  <div className="person-row__social">
                    {handles.map(([platform, handle]) => (
                      <span key={platform} className="person-row__social-handle" title={platform}>
                        <span aria-hidden="true">{SOCIAL_ICONS[platform] || '🔗'}</span>{handle}
                      </span>
                    ))}
                  </div>
                  <span className={`type-chip type-chip--${person.type}`}>
                    {t(`people.types.${person.type}`)}
                  </span>
                </button>
                {isOpen && (
                  <PersonEditForm
                    person={person}
                    organisations={organisations}
                    onSave={(updates) => handleSave(person.id, updates)}
                    onCancel={() => { setExpandedId(null); setEditError(null); }}
                    onDelete={() => setDeleteTarget(person)}
                    onOrgsRefresh={refreshOrgs}
                    saving={savingId === person.id}
                    error={editError}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      {showCreate && (
        <CreatePersonModal
          organisations={organisations}
          onOrgsRefresh={refreshOrgs}
          onClose={() => setShowCreate(false)}
          onCreated={handleCreated}
        />
      )}

      {deleteTarget && (
        <ConfirmDeleteDialog
          name={deleteTarget.name}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => handleDelete(deleteTarget.id)}
          deleting={deleting}
        />
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Page wrapper: title + tab bar (People | Organisations)
// ─────────────────────────────────────────────────────────────────────────────
export function PeopleList() {
  const { t } = useTranslation();
  const [tab, setTab] = useState('people');

  return (
    <div>
      <header className="page-header">
        <h1 className="page-header__title">{t('people.title')}</h1>
      </header>

      <div className="tabs" role="tablist" style={{ marginBottom: 'var(--space-5)' }}>
        {TAB_KEYS.map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            className={'tab' + (tab === key ? ' tab--active' : '')}
            onClick={() => setTab(key)}
          >
            {t(`people.tabs.${key}`)}
          </button>
        ))}
      </div>

      {tab === 'people' ? <PeopleSection /> : <OrganisationsList />}
    </div>
  );
}
